/* eslint-disable arrow-body-style */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { useUpdateValidation } from 'components/shared/hooks';
import { accountDetailsValidationSchema as validationSchema } from 'containers/shared/validationSchemas';
import type { StepComponentProps } from 'components/Stepper';
import type { CountryCodes } from 'state/countryCodes';

import useStyles from './AccountDetails.style';

type AccountDetailsProps = StepComponentProps & {
  countryCodes: CountryCodes | [];
  currentUser: string | null;
};

export type AccountDetailsFormValues = {
  username: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  country: number;
};

export const isAccountDetailsFormValues = (
  stepValues: unknown
): stepValues is AccountDetailsFormValues =>
  !!(stepValues as AccountDetailsFormValues).username &&
  !!(stepValues as AccountDetailsFormValues).firstName &&
  !!(stepValues as AccountDetailsFormValues).lastName &&
  !!(stepValues as AccountDetailsFormValues).phoneNumber &&
  !!(stepValues as AccountDetailsFormValues).country;

const defaultValues: AccountDetailsFormValues = {
  username: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  country: 0,
};

const CountryLabelText = { open: 'Country', close: 'Select Country' };

const AccountDetails: React.FC<AccountDetailsProps> = (props) => {
  const {
    onNext,
    updateValidation,
    formId,
    pending,
    countryCodes,
    currentUser,
  } = props;
  const { header, ...classes } = useStyles();
  const [countryLabelText, setCountryLabelText] = useState(
    CountryLabelText.close
  );
  const onCountryOpen = () => setCountryLabelText(CountryLabelText.open);
  const onCountryClose = () => setCountryLabelText(CountryLabelText.close);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm<AccountDetailsFormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  useUpdateValidation(updateValidation, isValid);

  // set username field (hidden form value)
  if (currentUser) {
    setValue('username', currentUser);
  }

  // TODO: pass this in as a prop?
  const onSubmit: SubmitHandler<AccountDetailsFormValues> = (values) => {
    if (onNext) {
      onNext(values);
    }
  };

  return (
    <>
      <Typography variant="h2" className={header}>
        Account Details
      </Typography>
      <form
        id={formId}
        className={classes.form}
        aria-label="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  autoComplete="given-name"
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  placeholder="First Name / Given Name"
                  autoFocus
                  disabled={pending}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  autoComplete="family-name"
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  placeholder="Last Name / Surname"
                  disabled={pending}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  autoComplete="tel"
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                  id="phoneNumber"
                  label="Phone Number"
                  placeholder="e.g. +1 123-456-7890"
                  // defaultValue=""
                  type="tel"
                  disabled={pending}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="country"
              control={control}
              defaultValue={undefined}
              render={({ field: { onBlur, ...field } }) => (
                <TextField
                  select
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                  label={countryLabelText}
                  placeholder="Select Country"
                  type="country-code"
                  id="countryCode"
                  disabled={pending}
                  error={!!errors.country}
                  helperText={errors.country?.message}
                  onBlur={(_event) => {
                    onCountryClose();
                    onBlur();
                  }}
                  SelectProps={{
                    onOpen: onCountryOpen,
                    onClose: onCountryClose,
                    MenuProps: {
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                    },
                  }}
                  {...field}
                >
                  {countryCodes.map(({ code, country }) => {
                    // TODO: get rid of out-of-range message by using lazy validation on Yup country field?
                    return code ? (
                      <MenuItem key={code} value={code}>
                        {country}
                      </MenuItem>
                    ) : undefined;
                  })}
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default AccountDetails;
